import React from 'react';

import { Link } from '@gogame/components';
import DataGrid from '@gogame/data-grid';
import { useChildComponent } from '@gogame/hooks-ts';
import { useScreenState, useScreenDispatch } from '@gogame/screens';

import PartnerUserActions from 'scenes/Users/PartnerUsers/components/Actions';

import {
  COLUMN_ID,
  COLUMN_NAME,
  COLUMN_BUSINESS_PARTNERS,
  COLUMN_EMAIL,
  COLUMN_LAST_ACTIVITY_DATE,
  COLUMN_REG_DATE,
  COLUMN_TEST,
} from 'scenes/Users/PartnerUsers/constants';
import { useLoadPartnerUsers } from 'scenes/Users/PartnerUsers/data';
import {
  SET_TABLE_SETTINGS,
  REORDER_COLUMNS,
  COLLAPSE_COLUMNS,
} from 'scenes/Users/PartnerUsers/screenReducer';

import SelectedRowsToolbarActions from './SelectedRowsToolbarActions';
import Toolbar from './Toolbar';
import {
  PartnerUserFormDuplicate,
  PartnerUserFormEdit,
} from '../PartnerUserForm';

const PartnerUsersDataTable = () => {
  const [childComponent, openChildComponent] = useChildComponent();
  const screenState = useScreenState();
  const dispatch = useScreenDispatch();

  const { data, isLoading, isRefetching } = useLoadPartnerUsers({
    tableSettings: screenState.tableSettings,
    searchQuery: screenState.searchQuery,
    ...screenState.filters,
  });

  const handleChangeSettings = (settings) => {
    dispatch({
      type: SET_TABLE_SETTINGS,
      payload: settings,
    });
  };

  const handleReorder = (order) => {
    dispatch({
      type: REORDER_COLUMNS,
      payload: order,
    });
  };

  const handleCollapse = (collapsed) => {
    dispatch({
      type: COLLAPSE_COLUMNS,
      payload: collapsed,
    });
  };

  const handleDuplicate = ({ id }) => {
    openChildComponent(PartnerUserFormDuplicate, {
      id,
    });
  };

  const handleEdit = ({ id }) => {
    openChildComponent(PartnerUserFormEdit, {
      id,
    });
  };

  return (
    <React.Fragment>
      {childComponent}
      <DataGrid
        selectedRowsToolbarActions={({ selectedValues }) => (
          <SelectedRowsToolbarActions
            selectedValues={selectedValues}
            openChildComponent={openChildComponent}
          />
        )}
        checkboxSelection={true}
        rows={data?.rows}
        count={data?.count}
        loading={isLoading}
        fetching={isRefetching}
        ready={screenState.ready}
        onChangeSettings={handleChangeSettings}
        onReorder={handleReorder}
        onCollapse={handleCollapse}
        tableSettings={screenState.tableSettings}
        columnsState={screenState.columns}
        toolbar={<Toolbar />}
        storageKey={'PARTNER_USERS_LOCAL_STORAGE'}
        defaultColumnWidth={160}
        filtered={!!screenState.searchQuery}
      >
        <DataGrid.Column
          field={COLUMN_ID}
          type={'text'}
          headerName={'ID'}
          width={90}
          fixed={true}
          resizable={false}
          align={'right'}
          headerAlign={'right'}
        />
        <DataGrid.Column
          field={COLUMN_NAME}
          headerName={'Full Name'}
          type={'text'}
          width={160}
          fixed={true}
          resizable={false}
        />
        <DataGrid.Column
          field={COLUMN_BUSINESS_PARTNERS}
          headerName={'Business Partners'}
          type={'list'}
          limit={1}
        />
        <DataGrid.Column
          field={COLUMN_EMAIL}
          headerName={'Email'}
          width={240}
          renderCell={({ value }) => {
            return (
              <Link href={`mailto:${value}`} target={'_blank'}>
                {value}
              </Link>
            );
          }}
        />
        <DataGrid.Column
          field={COLUMN_TEST}
          headerName={'Test'}
          type={'bool'}
          align={'center'}
        />
        <DataGrid.Column
          field={COLUMN_LAST_ACTIVITY_DATE}
          type={'date'}
          headerName={'Last Login'}
          template={'LLL'}
          timeZone={'local'}
        />
        <DataGrid.Column
          field={COLUMN_REG_DATE}
          type={'date'}
          headerName={'Created'}
          template={'LLL'}
          timeZone={'local'}
        />
        <DataGrid.Column
          type={'actions'}
          actionsColDef
          limit={3}
          actionsComponent={PartnerUserActions}
          onEdit={handleEdit}
          onDuplicate={handleDuplicate}
        />
      </DataGrid>
    </React.Fragment>
  );
};

export default PartnerUsersDataTable;
